/**
 * AdminQuickAddPage.jsx — Paste an Amazon URL → instant affiliate listing.
 *
 * Routes here at /admin/quick-add.
 *
 * Why this exists
 * ───────────────
 * SerpAPI is fuzzy — searches for "Daiwa BG Spinning Reel" can match a
 * $29.99 replacement handle knob instead of the $199 reel. This tool
 * lets the admin paste the EXACT Amazon URL of a product their friends
 * or community already want to buy, then we:
 *   1. Extract the ASIN
 *   2. Rebuild the URL with our affiliate tag
 *   3. Scrape the og:image + og:title for a clean preview
 *   4. Save to hp_amazon_products (manual_verified=true)
 *   5. Optionally queue a marketing video render in the background
 *
 * This is the TOS-safe path to Amazon Associate's 3 qualifying sales gate.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE } from "../../lib/api";
import { toast } from "sonner";

const CATEGORIES = [
  "fishing", "camping", "marine", "home-garden",
  "kitchen", "electronics", "sports-fitness", "tools-diy", "lifestyle",
];

export default function AdminQuickAddPage() {
  const [url, setUrl]                 = useState("");
  const [preview, setPreview]         = useState(null);
  const [titleOverride, setTitleOverride] = useState("");
  const [imageOverride, setImageOverride] = useState("");
  const [priceOverride, setPriceOverride] = useState("");
  const [category, setCategory]       = useState("lifestyle");
  const [tagsCSV, setTagsCSV]         = useState("");
  const [featured, setFeatured]       = useState(false);
  const [cheapo, setCheapo]           = useState(false);
  const [autoVideo, setAutoVideo]     = useState(true);
  const [bgMode, setBgMode]           = useState("auto");
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [savedLog, setSavedLog]       = useState([]);

  const fetchPreview = async () => {
    if (!url.trim()) {
      toast.error("Paste an Amazon URL first");
      return;
    }
    setLoading(true);
    setPreview(null);
    try {
      const pu = new URL(`${API_BASE}/admin/hustleshop/quick-add/preview`, window.location.origin);
      pu.searchParams.set("url", url.trim());
      const r = await fetch(pu, { credentials: "include" });
      const j = await r.json();
      if (!r.ok) {
        toast.error(j?.detail || "Preview failed");
        return;
      }
      setPreview(j);
      if (!j.title)    setTitleOverride("");
      if (!j.image_url) setImageOverride("");
    } catch (e) {
      toast.error("Network error fetching preview");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!preview) {
      toast.error("Run preview first");
      return;
    }
    setSaving(true);
    try {
      const body = {
        amazon_url: url.trim(),
        category,
        tags: tagsCSV.split(",").map(t => t.trim()).filter(Boolean),
        featured,
        cheapo,
        auto_generate_video: autoVideo,
        bg_mode: bgMode,
      };
      if (titleOverride.trim()) body.title_override = titleOverride.trim();
      if (imageOverride.trim()) body.image_override = imageOverride.trim();
      if (priceOverride.trim()) body.price_override = parseFloat(priceOverride);

      const r = await fetch(`${API_BASE}/admin/hustleshop/quick-add`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) {
        toast.error(j?.detail || "Save failed");
        return;
      }
      toast.success(`Saved ${j.slug}${j.video_queued ? " — video queued" : ""}`);
      setSavedLog(prev => [{
        slug: j.slug, title: j.title, asin: j.asin,
        url: j.amazon_url, price: j.price_display,
        band: j.price_band, video_queued: j.video_queued,
        at: new Date().toISOString(),
      }, ...prev].slice(0, 20));
      // Reset for next paste
      setUrl(""); setPreview(null);
      setTitleOverride(""); setImageOverride(""); setPriceOverride("");
    } catch (e) {
      toast.error("Network error saving");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="admin-quick-add-page">
      <div className="mb-6 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Quick-Add from Amazon URL
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Paste a verified Amazon product URL — we&apos;ll extract the ASIN,
            apply the affiliate tag, fetch a preview, and save it to HustleShop.
          </p>
        </div>
        <Link to="/admin/video-studio"
              className="text-xs text-amber-600 hover:underline"
              data-testid="link-video-studio">
          → Video Studio
        </Link>
      </div>

      {/* ─── URL paste + preview ─── */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 mb-5">
        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
          Amazon Product URL
        </label>
        <div className="flex gap-2">
          <input
            type="url"
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://www.amazon.com.au/dp/B0XXXXXXXX/..."
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono"
            data-testid="input-amazon-url"
          />
          <button
            onClick={fetchPreview}
            disabled={loading}
            className="px-5 py-2 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-700 disabled:opacity-50"
            data-testid="btn-fetch-preview">
            {loading ? "Fetching…" : "Preview"}
          </button>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">
          Accepted shapes: <code>/dp/ASIN</code>, <code>/gp/product/ASIN</code>,
          {" "}<code>?asin=ASIN</code>. The affiliate tag is appended automatically.
        </p>
      </div>

      {/* ─── Preview card ─── */}
      {preview && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 mb-5"
             data-testid="quick-add-preview">
          <div className="flex gap-5">
            <div className="w-40 shrink-0">
              {preview.image_url ? (
                <img src={imageOverride || preview.image_url}
                     alt={preview.title}
                     className="w-40 h-40 object-contain border border-slate-200 rounded-lg bg-white" />
              ) : (
                <div className="w-40 h-40 grid place-items-center border-2 border-dashed border-rose-300 rounded-lg text-rose-500 text-xs text-center px-2">
                  No image scraped — paste an image_override URL below
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-slate-500 font-mono">
                ASIN <span className="font-bold text-slate-900">{preview.asin}</span>
                {" · "}{preview.amazon_domain}
              </div>
              <h2 className="text-lg font-semibold text-slate-900 mt-1 leading-tight">
                {titleOverride || preview.title || "(no title scraped)"}
              </h2>
              {preview.price_amount !== null && preview.price_amount !== undefined && (
                <div className="text-xl font-bold text-emerald-600 mt-2">
                  ${preview.price_amount.toFixed(2)}
                </div>
              )}
              <a href={preview.affiliate_url} target="_blank" rel="noreferrer"
                 className="inline-block text-[11px] text-amber-600 hover:underline mt-3 break-all">
                {preview.affiliate_url}
              </a>
              {preview.scrape_error && (
                <div className="text-[11px] text-rose-500 mt-2">
                  ⚠ Scrape error: {preview.scrape_error}. Use overrides below.
                </div>
              )}
            </div>
          </div>

          {/* ─── Overrides & metadata ─── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5 pt-5 border-t border-slate-100">
            <Override label="Title override (optional)"
                      value={titleOverride} onChange={setTitleOverride}
                      placeholder={preview.title}
                      testid="input-title-override" />
            <Override label="Image URL override (optional)"
                      value={imageOverride} onChange={setImageOverride}
                      placeholder={preview.image_url || "https://…"}
                      testid="input-image-override" />
            <Override label="Price override (AUD)"
                      value={priceOverride} onChange={setPriceOverride}
                      placeholder={String(preview.price_amount || "")}
                      testid="input-price-override" type="number" />

            <Select label="Category" value={category} onChange={setCategory}
                    options={CATEGORIES} testid="select-category" />
            <Override label="Tags (comma-separated)" value={tagsCSV}
                      onChange={setTagsCSV}
                      placeholder="fishing, reels, shimano"
                      testid="input-tags" />
            <Select label="Background mode" value={bgMode} onChange={setBgMode}
                    options={["auto", "image", "video"]}
                    testid="select-bg-mode" />

            <Toggle label="Featured" value={featured} onChange={setFeatured}
                    testid="toggle-featured" />
            <Toggle label="Cheap-o (under $10)" value={cheapo} onChange={setCheapo}
                    testid="toggle-cheapo" />
            <Toggle label="Auto-generate video" value={autoVideo}
                    onChange={setAutoVideo} testid="toggle-auto-video" />
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <button onClick={() => { setPreview(null); setUrl(""); }}
                    className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900"
                    data-testid="btn-cancel">
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="px-6 py-2 bg-amber-400 text-slate-900 text-sm font-semibold rounded-lg hover:bg-amber-300 disabled:opacity-50"
              data-testid="btn-save-quick-add">
              {saving ? "Saving…" : autoVideo ? "Save + Queue Video" : "Save"}
            </button>
          </div>
        </div>
      )}

      {/* ─── Recently added log ─── */}
      {savedLog.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5"
             data-testid="quick-add-log">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">
            Just added (this session)
          </h3>
          <ul className="space-y-2 text-xs">
            {savedLog.map(s => (
              <li key={s.slug + s.at}
                  className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-50">
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{s.title}</div>
                  <div className="text-slate-500 font-mono">
                    {s.asin} · {s.band} · {s.price || "—"}
                    {s.video_queued && (
                      <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">
                        video queued
                      </span>
                    )}
                  </div>
                </div>
                <a href={s.url} target="_blank" rel="noreferrer"
                   className="text-amber-600 hover:underline shrink-0">
                  open ↗
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Override({ label, value, onChange, placeholder, testid, type = "text" }) {
  return (
    <label className="block text-xs">
      <div className="font-semibold text-slate-600 uppercase tracking-wide mb-1">
        {label}
      </div>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
             placeholder={placeholder}
             data-testid={testid}
             className="w-full px-2.5 py-1.5 border border-slate-300 rounded-md text-sm" />
    </label>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block text-xs">
      <div className="font-semibold text-slate-600 uppercase tracking-wide mb-1">
        {label}
      </div>
      <select value={value} onChange={e => onChange(e.target.value)}
              data-testid={testid}
              className="w-full px-2.5 py-1.5 border border-slate-300 rounded-md text-sm bg-white">
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function Toggle({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-xs select-none cursor-pointer mt-5">
      <input type="checkbox" checked={value}
             onChange={e => onChange(e.target.checked)}
             data-testid={testid}
             className="rounded border-slate-300" />
      <span className="text-slate-700 font-medium">{label}</span>
    </label>
  );
}
